import { defineStore } from 'pinia'

import { computed, reactive, ref } from 'vue'

import { AuthApi, RegisterType } from '@/features/api/services/authApi.js'
import { useAuthStore } from '@/features/auth/useAuthStore.js'
import { formatRegisterDateOfBirth } from '@/features/auth/utils/formatRegisterDateOfBirth.js'
import { connectInvitationForAuthenticatedUser } from '@/features/auth/utils/connectInvitationForAuthenticatedUser.js'
import {
  formatInvitationCodeInput,
  isInvitationCodeComplete,
} from '@/features/auth/utils/invitationCodeFormat.js'
import { parseWebRegisterResponse } from '@/features/auth/utils/parseWebRegisterResponse.js'
import { isVerifyEmailCodeSuccess } from '@/features/auth/utils/parseVerifyEmailResponse.js'
import {
  getEmailFromQuery,
  getInvitationCodeFromQuery,
  isInvitedRegistrationQuery,
  pickInvitationQueryForRoute,
} from '@/features/auth/utils/authInvitationQuery.js'
import {
  getAuthApiErrorCode,
  isRegisterEmailAlreadyExistsError,
  resolveRegisterApiErrorI18nKey,
  resolveRegisterFailureResponseI18nKey,
} from '@/features/auth/utils/resolveRegisterApiError.js'
import { getRegisterPasswordValidationErrorI18nKey } from '@/features/auth/utils/registerPasswordValidation.js'
import {
  AUTH_FLOW,
  addAuthFlowBreadcrumb,
  reportAuthFlowError,
} from '@/features/auth/utils/reportAuthFlowError.js'
import { useToaster } from '@/features/ui/toaster/useToaster.js'
import {
  AlreadyRegisteredThroughGoogleMessage,
  useLoginStore,
} from '@/features/auth/views/login/useLoginStore.js'
import { useClubRegisterStore } from '@/features/clubRegister/views/useClubRegisterStore.js'
import { CLUB_SELECTOR_PATH } from '@/features/auth/clubSelfRegistrationConfig.js'
import { getActiveClubId } from '@/features/router/useActiveClub.js'
import { useClubRouter } from '@/features/router/useClubRouer.js'

export const REGISTER_STEPS = {
  EMAIL: 1,
  OTP: 2,
  PERSONAL: 3,
  INVITATION: 4,
}

export const REGISTER_STEP_COUNT = 4

function getRegisterStepLabel(step) {
  switch (step) {
    case REGISTER_STEPS.EMAIL:
      return 'email'
    case REGISTER_STEPS.OTP:
      return 'otp'
    case REGISTER_STEPS.PERSONAL:
      return 'personal'
    case REGISTER_STEPS.INVITATION:
      return 'invitation'
    default:
      return String(step)
  }
}

export const useRegisterStore = defineStore('registerStore', function () {
  const toaster = useToaster()
  const authStore = useAuthStore()
  const loginStore = useLoginStore()
  const clubRegisterStore = useClubRegisterStore()
  const router = useClubRouter()

  const currentStep = ref(REGISTER_STEPS.EMAIL)
  const isLoading = ref(false)
  const errorText = ref('')
  const isInvitedRegistration = ref(false)
  const verificationEmail = ref('')

  const formValues = reactive({
    email: '',
    otpCode: '',
    firstName: '',
    lastName: '',
    dateOfBirth: null,
    gender: null,
    password: '',
    retypePassword: '',
    privacyPolicyAccepted: false,
    invitationCode: '',
  })

  const isEmailStep = computed(() => currentStep.value === REGISTER_STEPS.EMAIL)
  const isOtpStep = computed(() => currentStep.value === REGISTER_STEPS.OTP)
  const isPersonalStep = computed(() => currentStep.value === REGISTER_STEPS.PERSONAL)
  const isInvitationStep = computed(() => currentStep.value === REGISTER_STEPS.INVITATION)

  function clearError() {
    errorText.value = ''
  }

  async function redirectToLoginForExistingEmail() {
    const email = formValues.email.trim()

    loginStore.setFormValues({ email })
    toaster.warn({ detail: 'register.emailAlreadyExists' })

    await router.push({
      path: '/auth/login',
      query: pickInvitationQueryForRoute({
        ...router.currentRoute.value.query,
        email: email || getEmailFromQuery(router.currentRoute.value.query),
      }),
    })
  }

  function hydrateFromInvitationQuery(query = {}) {
    const invitationCode = getInvitationCodeFromQuery(query)
    const email = getEmailFromQuery(query)

    if (email) {
      formValues.email = email
    }

    if (invitationCode) {
      formValues.invitationCode = invitationCode
    }

    isInvitedRegistration.value = isInvitedRegistrationQuery(query)
  }

  function initializeRegisterFromRoute(query = {}) {
    resetRegistration()
    hydrateFromInvitationQuery(query)
  }

  function setEmail(email) {
    formValues.email = email
  }

  function resetRegistration() {
    currentStep.value = REGISTER_STEPS.EMAIL
    isLoading.value = false
    errorText.value = ''
    formValues.email = ''
    formValues.otpCode = ''
    formValues.firstName = ''
    formValues.lastName = ''
    formValues.dateOfBirth = null
    formValues.gender = null
    formValues.password = ''
    formValues.retypePassword = ''
    formValues.privacyPolicyAccepted = false
    formValues.invitationCode = ''
    isInvitedRegistration.value = false
    verificationEmail.value = ''
  }

  function validateCurrentStep() {
    clearError()

    if (currentStep.value === REGISTER_STEPS.EMAIL) {
      const email = formValues.email.trim()
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

      if (!email) {
        errorText.value = 'validation.requiredEmail'
        return false
      }

      if (!emailRegex.test(email)) {
        errorText.value = 'validation.invalidEmail'
        return false
      }

      return true
    }

    if (currentStep.value === REGISTER_STEPS.OTP) {
      if (!formValues.otpCode || formValues.otpCode.length !== 6) {
        errorText.value = 'register.otpInvalid'
        return false
      }

      return true
    }

    if (currentStep.value === REGISTER_STEPS.PERSONAL) {
      if (!formValues.firstName.trim()) {
        errorText.value = 'register.firstNameRequired'
        return false
      }

      if (!formValues.lastName.trim()) {
        errorText.value = 'register.lastNameRequired'
        return false
      }

      if (!formValues.dateOfBirth) {
        errorText.value = 'register.dateOfBirthRequired'
        return false
      }

      if (formValues.gender === null || formValues.gender === undefined) {
        errorText.value = 'register.genderRequired'
        return false
      }

      if (!formValues.password) {
        errorText.value = 'validation.required'
        return false
      }

      const passwordValidationError = getRegisterPasswordValidationErrorI18nKey(formValues.password)

      if (passwordValidationError) {
        errorText.value = passwordValidationError
        return false
      }

      if (formValues.password !== formValues.retypePassword) {
        errorText.value = 'register.passwordMismatch'
        return false
      }

      if (!formValues.privacyPolicyAccepted) {
        errorText.value = 'register.privacyRequired'
        return false
      }

      return true
    }

    if (currentStep.value === REGISTER_STEPS.INVITATION) {
      const code = formatInvitationCodeInput(formValues.invitationCode)

      if (isInvitedRegistration.value && !isInvitationCodeComplete(code)) {
        errorText.value = 'clubSelector.invitationModal.codeIncomplete'
        return false
      }

      if (code && !isInvitationCodeComplete(code)) {
        errorText.value = 'clubSelector.invitationModal.codeIncomplete'
        return false
      }

      return true
    }

    return true
  }

  async function finishRegistrationNavigation() {
    await loginStore.decideNavigation({ omitInvitationFromRoute: true })
  }

  async function connectInvitationCodeIfPresent() {
    const normalizedCode = formatInvitationCodeInput(formValues.invitationCode)

    if (!normalizedCode) {
      return true
    }

    const result = await connectInvitationForAuthenticatedUser(normalizedCode)

    if (result.success) {
      return true
    }

    if (result.reason === 'incompleteCode') {
      errorText.value = 'clubSelector.invitationModal.codeIncomplete'
      return false
    }

    errorText.value = result.i18nKey || 'clubSelector.invitationErrors.generic'
    return false
  }

  async function goToNextStep() {
    if (!validateCurrentStep()) {
      return false
    }

    isLoading.value = true
    clearError()

    try {
      if (currentStep.value === REGISTER_STEPS.EMAIL) {
        const email = formValues.email.trim()
        formValues.email = email
        await AuthApi.sendEmailVerification(email)
        verificationEmail.value = email
        currentStep.value = REGISTER_STEPS.OTP
        addAuthFlowBreadcrumb({
          flow: AUTH_FLOW.REGISTER,
          step: getRegisterStepLabel(REGISTER_STEPS.OTP),
          message: '[Auth] register advanced to otp',
        })
        return true
      }

      if (currentStep.value === REGISTER_STEPS.OTP) {
        const email = verificationEmail.value || formValues.email.trim()
        const verifyResponse = await AuthApi.verifyEmailCode({
          email,
          code: formValues.otpCode,
        })

        if (!isVerifyEmailCodeSuccess(verifyResponse)) {
          errorText.value = 'register.otpInvalid'
          reportAuthFlowError({
            flow: AUTH_FLOW.REGISTER,
            step: getRegisterStepLabel(REGISTER_STEPS.OTP),
            reason: 'otp-invalid',
            i18nKey: errorText.value,
          })
          return false
        }

        currentStep.value = REGISTER_STEPS.PERSONAL
        addAuthFlowBreadcrumb({
          flow: AUTH_FLOW.REGISTER,
          step: getRegisterStepLabel(REGISTER_STEPS.PERSONAL),
          message: '[Auth] register advanced to personal',
        })
        return true
      }

      if (currentStep.value === REGISTER_STEPS.PERSONAL) {
        const registerEmail = verificationEmail.value || formValues.email.trim()
        const registerResponse = await AuthApi.registerWebDirect({
          email: registerEmail,
          password: formValues.password,
          firstName: formValues.firstName.trim(),
          lastName: formValues.lastName.trim(),
          dateOfBirth: formatRegisterDateOfBirth(formValues.dateOfBirth),
          gender: formValues.gender,
          registerType: RegisterType.TenantUser,
          tenantId: getActiveClubId(),
        })

        const parsedRegisterResponse = parseWebRegisterResponse(registerResponse)

        if (parsedRegisterResponse.isFailure) {
          errorText.value = resolveRegisterFailureResponseI18nKey(parsedRegisterResponse.raw)
          reportAuthFlowError({
            flow: AUTH_FLOW.REGISTER,
            step: getRegisterStepLabel(REGISTER_STEPS.PERSONAL),
            reason: 'register-failure-response',
            errorCode:
              typeof parsedRegisterResponse.raw === 'string' ? parsedRegisterResponse.raw : null,
            i18nKey: errorText.value,
          })
          return false
        }

        if (parsedRegisterResponse.isAlreadyRegistered) {
          await redirectToLoginForExistingEmail()
          return false
        }

        if (!parsedRegisterResponse.authToken) {
          errorText.value = 'register.registerError'
          reportAuthFlowError({
            flow: AUTH_FLOW.REGISTER,
            step: getRegisterStepLabel(REGISTER_STEPS.PERSONAL),
            reason: 'missing-auth-token',
            i18nKey: errorText.value,
          })
          return false
        }

        await authStore.setUserToken(parsedRegisterResponse.authToken, {
          email: registerEmail,
          password: formValues.password,
        })

        if (isInvitedRegistration.value) {
          addAuthFlowBreadcrumb({
            flow: AUTH_FLOW.REGISTER,
            step: getRegisterStepLabel(REGISTER_STEPS.PERSONAL),
            message: '[Auth] register redirect to club-selector for auto-invitation',
          })

          await router.push({
            path: CLUB_SELECTOR_PATH,
            query: pickInvitationQueryForRoute({
              ...router.currentRoute.value.query,
              email: registerEmail,
            }),
          })
          return true
        }

        currentStep.value = REGISTER_STEPS.INVITATION
        addAuthFlowBreadcrumb({
          flow: AUTH_FLOW.REGISTER,
          step: getRegisterStepLabel(REGISTER_STEPS.INVITATION),
          message: '[Auth] register advanced to invitation',
        })
        return true
      }

      if (currentStep.value === REGISTER_STEPS.INVITATION) {
        const connected = await connectInvitationCodeIfPresent()

        if (!connected) {
          return false
        }

        await finishRegistrationNavigation()
        return true
      }

      return false
    } catch (error) {
      const errorCode = getAuthApiErrorCode(error)
      const step = getRegisterStepLabel(currentStep.value)

      if (AlreadyRegisteredThroughGoogleMessage === errorCode) {
        addAuthFlowBreadcrumb({
          flow: AUTH_FLOW.REGISTER,
          step,
          message: '[Auth] register redirect to google login warning',
        })
        clubRegisterStore.setAuthFlow(clubRegisterStore.AUTH_FLOW.LOGIN)
        loginStore.setCurrentStep(loginStore.STEPS.GOOGLE_LOGIN_WARNING)
        await router.push('/auth/login')
        return false
      }

      if (isRegisterEmailAlreadyExistsError(errorCode)) {
        addAuthFlowBreadcrumb({
          flow: AUTH_FLOW.REGISTER,
          step,
          message: '[Auth] register redirect to login for existing email',
        })
        await redirectToLoginForExistingEmail()
        return false
      }

      errorText.value = resolveRegisterApiErrorI18nKey(error)

      reportAuthFlowError({
        flow: AUTH_FLOW.REGISTER,
        step,
        error,
        errorCode,
        i18nKey: errorText.value,
      })
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function skipInvitationStep() {
    if (currentStep.value !== REGISTER_STEPS.INVITATION) {
      return false
    }

    if (isInvitedRegistration.value) {
      errorText.value = 'register.invitationRequired'
      return false
    }

    isLoading.value = true
    clearError()

    try {
      await loginStore.decideNavigation({ omitInvitationFromRoute: true })
      return true
    } catch (error) {
      errorText.value = resolveRegisterApiErrorI18nKey(error)
      reportAuthFlowError({
        flow: AUTH_FLOW.REGISTER,
        step: getRegisterStepLabel(REGISTER_STEPS.INVITATION),
        error,
        reason: 'skip-invitation-navigation',
        i18nKey: errorText.value,
      })
      return false
    } finally {
      isLoading.value = false
    }
  }

  function goToPreviousStep() {
    clearError()

    if (currentStep.value === REGISTER_STEPS.OTP) {
      verificationEmail.value = ''
      formValues.otpCode = ''
    }

    if (currentStep.value > REGISTER_STEPS.EMAIL) {
      currentStep.value -= 1
    }
  }

  return {
    REGISTER_STEPS,
    REGISTER_STEP_COUNT,
    currentStep,
    formValues,
    isLoading,
    errorText,
    isInvitedRegistration,
    verificationEmail,
    isEmailStep,
    isOtpStep,
    isPersonalStep,
    isInvitationStep,
    clearError,
    setEmail,
    hydrateFromInvitationQuery,
    initializeRegisterFromRoute,
    resetRegistration,
    goToNextStep,
    goToPreviousStep,
    skipInvitationStep,
  }
})
